import React, { useCallback, useState, useRef } from 'react';
import {
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  View,
  TouchableOpacity,
} from 'react-native';
import { Text } from '@/components/common/text';
import { StackScreenProps } from '@react-navigation/stack';

import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { ActionButtons, FormInput, FormSection } from '@/components';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { useEditConversationMutation } from '@/store/api/admin/messagesApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { SVG_COMPONENTS } from '@/assets/admin';
import { RecipientSelector } from '@/components/admin/messages/CreateMessage/components/RecipientSelector';
import { useEditMessage } from '@/components/admin/messages/CreateMessage/hooks/useEditMessage';
import { useConversation } from '@/hooks/admin/useMessages';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopFormPage } from '@/components/common/admin/desktop/DesktopFormPage';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard } from '@/components/shared/redesign';

type EditMessageScreenProps = StackScreenProps<AdminStackParamList, 'EditMessage'>;

export const EditMessageScreen: React.FC<EditMessageScreenProps> = ({
  navigation,
  route,
}) => {
  const { redesign, admin } = useAdminColors();
  const { conversationId } = route.params;
  const { layoutMode } = useResponsive();
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;

  const { conversation, isLoading: conversationLoading } = useConversation(conversationId);
  const [editConversation, { isLoading }] = useEditConversationMutation();
  const scrollViewRef = useRef<ScrollView>(null);

  // Custom hook for edit form logic
  const {
    formData,
    errors,
    validateForm,
    handleFieldChange,
    showDiscardAlert,
    hasUnsavedChanges,
    getNewRecipients,
  } = useEditMessage(conversation);

  // Form submission handler
  const handleSubmit = useCallback(async () => {
    if (!selectedPropertyId || !conversation) {
      showApiError('No property or conversation selected');
      return;
    }

    // Check if form is valid before submission
    if (isLoading || !formData.subject || formData.recipients.length === 0) {
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      showApiError('Please fix the form errors before submitting');
      return;
    }

    try {
      // Create FormData matching admin-web implementation
      const formDataToSend = new FormData();

      // Basic fields
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('sendEmail', formData.sendEmail.toString());
      formDataToSend.append('propertyId', selectedPropertyId.toString());

      // Get only new recipients (not in original list)
      const newRecipients = getNewRecipients();
      const recipientLeaseCustomers = newRecipients.flatMap((r: any) => r.leaseCustomers?.[0] || r);

      const assignedRecipients = recipientLeaseCustomers
        .filter((r: any) => r.account && Object.keys(r.account || {}).length > 0)
        .map((r: any) => r.id);

      const unassignedRecipients = recipientLeaseCustomers
        .filter((r: any) => !r.account || Object.keys(r.account || {}).length <= 0)
        .map((r: any) => r.id);

      formDataToSend.append('recipients', JSON.stringify(assignedRecipients));
      formDataToSend.append('unassignedRecipients', JSON.stringify(unassignedRecipients));

      const result = await editConversation({
        body: formDataToSend,
        conversationId: conversation.id,
      }).unwrap();

      if (result?.success) {
        showSuccess('Conversation updated successfully');
        navigation.goBack();
      } else {
        showApiError('Failed to update conversation');
      }
    } catch (error: any) {
      console.error('Edit conversation error:', error);
      showApiError(error?.data?.message || 'Failed to update conversation');
    }
  }, [
    selectedPropertyId,
    conversation,
    validateForm,
    formData,
    editConversation,
    showSuccess,
    showApiError,
    navigation,
    getNewRecipients,
  ]);

  // Handle back navigation with unsaved changes check
  const handleBack = useCallback(() => {
    if (hasUnsavedChanges()) {
      showDiscardAlert(() => navigation.goBack());
    } else {
      navigation.goBack();
    }
  }, [hasUnsavedChanges, showDiscardAlert, navigation]);

  // Show loading state while conversation is loading
  if (conversationLoading || !conversation) {
    return (
      <AdminErrorBoundary context="EditMessageScreen">
        <View style={[styles.loadingContainer, { backgroundColor: redesign.bg.base }]}>
          <Text style={[styles.loadingText, { color: redesign.text.muted }]}>
            Loading conversation...
          </Text>
        </View>
      </AdminErrorBoundary>
    );
  }

  // Desktop layout
  if (layoutMode === 'desktop') {
    return (
      <AdminErrorBoundary context="EditMessageScreenDesktop">
        <DesktopFormPage
          title="Edit Conversation"
          onSubmit={handleSubmit}
          onCancel={handleBack}
          isSubmitting={isLoading}
          submitText="Update"
          contentMaxWidth={720}
        >
          <View style={styles.desktopSection}>
            <GlassCard padding={16}>
              <Text style={[styles.desktopSectionTitle, { color: redesign.text.primary }]}>
                Conversation Details
              </Text>
              <FormInput
                label="Subject"
                value={formData.subject}
                onChangeText={(value) => handleFieldChange('subject', value)}
                placeholder="Enter conversation subject"
                error={errors.subject}
                required
                maxLength={100}
              />
            </GlassCard>
          </View>

          <View style={styles.desktopSection}>
            <GlassCard padding={16}>
              <Text style={[styles.desktopSectionTitle, { color: redesign.text.primary }]}>
                Recipients
              </Text>
              <RecipientSelector
                recipients={formData.recipients}
                onRecipientsChange={(recipients) => handleFieldChange('recipients', recipients)}
                error={errors.recipients}
                messageType="groupChat"
                isEditMode={true}
                originalRecipients={formData.originalRecipients}
              />
            </GlassCard>
          </View>

          <View style={styles.desktopSection}>
            <GlassCard padding={16}>
              <Text style={[styles.desktopSectionTitle, { color: redesign.text.primary }]}>
                Notification Settings
              </Text>
              <TouchableOpacity
                style={styles.toggleRow}
                onPress={() => handleFieldChange('sendEmail', !formData.sendEmail)}
              >
                <View style={styles.toggleContent}>
                  <Text style={[styles.toggleLabel, { color: redesign.text.primary }]}>
                    Send email notification
                  </Text>
                  <Text style={[styles.toggleSubtext, { color: redesign.text.muted }]}>
                    New recipients will receive an email notification about this conversation
                  </Text>
                </View>
                <View
                  style={[
                    styles.toggle,
                    { borderColor: redesign.card.border },
                    formData.sendEmail && {
                      backgroundColor: admin.formInput.borderFocused,
                      borderColor: admin.formInput.borderFocused,
                    },
                  ]}
                >
                  {formData.sendEmail && (
                    <SVG_COMPONENTS.CHECK_CIRCLE
                      width={16}
                      height={16}
                      color="#FFFFFF"
                    />
                  )}
                </View>
              </TouchableOpacity>
            </GlassCard>
          </View>

          <View style={[styles.infoSection, { marginTop: 0 }]}>
            <GlassCard padding={16}>
              <View style={styles.infoRow}>
                <SVG_COMPONENTS.X_CIRCLE
                  width={16}
                  height={16}
                  color={redesign.text.muted}
                />
                <Text style={[styles.infoText, { color: redesign.text.muted }]}>
                  You can only add new recipients to this conversation. Existing recipients cannot be removed.
                </Text>
              </View>
            </GlassCard>
          </View>
        </DesktopFormPage>
      </AdminErrorBoundary>
    );
  }

  // Mobile layout
  return (
    <AdminErrorBoundary context="EditMessageScreen">
      <View style={[styles.container, { backgroundColor: redesign.bg.base }]}>
        {/* Custom Header */}
        <View
          style={[
            styles.header,
            {
              backgroundColor: redesign.bg.base,
              borderBottomColor: redesign.card.border,
            },
          ]}
        >
          <TouchableOpacity
            style={styles.backButton}
            onPress={handleBack}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <SVG_COMPONENTS.CHEVRON_LEFT
              width={24}
              height={24}
              color={redesign.text.primary}
            />
          </TouchableOpacity>

          <Text style={[styles.headerTitle, { color: redesign.text.primary }]}>
            Edit Conversation
          </Text>

          <View style={styles.headerSpacer} />
        </View>

        {/* Main Content */}
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.keyboardAvoidingView}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 100 : 0}
        >
          <ScrollView
            ref={scrollViewRef}
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* Subject Section */}
            <FormSection title="Conversation Details">
              <FormInput
                label="Subject"
                value={formData.subject}
                onChangeText={(value) => handleFieldChange('subject', value)}
                placeholder="Enter conversation subject"
                error={errors.subject}
                required
                maxLength={100}
              />
            </FormSection>

            {/* Recipients Section */}
            <RecipientSelector
              recipients={formData.recipients}
              onRecipientsChange={(recipients) => handleFieldChange('recipients', recipients)}
              error={errors.recipients}
              messageType="groupChat" // Edit mode always uses group chat
              isEditMode={true}
              originalRecipients={formData.originalRecipients}
            />

            {/* Email Notification Toggle */}
            <FormSection title="Notification Settings">
              <TouchableOpacity
                onPress={() => handleFieldChange('sendEmail', !formData.sendEmail)}
                activeOpacity={0.8}
              >
                <GlassCard padding={16} style={styles.toggleCardMobile}>
                  <View style={styles.toggleRow}>
                    <View style={styles.toggleContent}>
                      <Text style={[styles.toggleLabel, { color: redesign.text.primary }]}>
                        Send email notification
                      </Text>
                      <Text style={[styles.toggleSubtext, { color: redesign.text.muted }]}>
                        New recipients will receive an email notification about this conversation
                      </Text>
                    </View>
                    <View
                      style={[
                        styles.toggle,
                        { borderColor: redesign.card.border },
                        formData.sendEmail && {
                          backgroundColor: admin.formInput.borderFocused,
                          borderColor: admin.formInput.borderFocused,
                        },
                      ]}
                    >
                      {formData.sendEmail && (
                        <SVG_COMPONENTS.CHECK_CIRCLE
                          width={16}
                          height={16}
                          color="#FFFFFF"
                        />
                      )}
                    </View>
                  </View>
                </GlassCard>
              </TouchableOpacity>
            </FormSection>

            {/* Info Section */}
            <View style={styles.infoSection}>
              <GlassCard padding={16}>
                <View style={styles.infoRow}>
                  <SVG_COMPONENTS.X_CIRCLE
                    width={16}
                    height={16}
                    color={redesign.text.muted}
                  />
                  <Text style={[styles.infoText, { color: redesign.text.muted }]}>
                    You can only add new recipients to this conversation. Existing recipients cannot be removed.
                  </Text>
                </View>
              </GlassCard>
            </View>
          </ScrollView>

          {/* Action Buttons: in the layout flow below the scroll view (as on
              the Create form), not the component's default overlay. Overlaid,
              its translucent glass showed the form scrolling underneath, and
              its real height (buttons + home-indicator inset) exceeded the
              clearance the scroll content reserved, hiding the last card. */}
          <ActionButtons
            isLoading={isLoading}
            onCancel={handleBack}
            onSubmit={handleSubmit}
            submitText="Update"
            cancelText="Cancel"
            containerStyle={styles.actionButtonsFlow}
          />
        </KeyboardAvoidingView>
      </View>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 16,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    paddingTop: 50,
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
  },
  headerSpacer: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 16,
  },
  actionButtonsFlow: {
    position: 'relative',
  },
  desktopSection: {
    marginBottom: 12,
  },
  desktopSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  toggleCardMobile: {
    marginTop: 8,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  toggleContent: {
    flex: 1,
    marginRight: 16,
  },
  toggleLabel: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 4,
  },
  toggleSubtext: {
    fontSize: 14,
    lineHeight: 18,
  },
  toggle: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoSection: {
    marginTop: 16,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 18,
    marginLeft: 8,
  },
});
